'use client';

import React, { useEffect } from 'react';
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle, XCircle } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';

// Shared variant styles for consistency with Alert
const variants = {
    default: {
        icon: <Info className="w-5 h-5 text-gray-500" />,
        border: 'border-gray-200 dark:border-gray-800',
        progress: 'bg-gray-500'
    },
    success: {
        icon: <CheckCircle2 className="w-5 h-5 text-green-500" />,
        border: 'border-green-100 dark:border-green-900/30',
        progress: 'bg-green-500'
    },
    error: {
        icon: <XCircle className="w-5 h-5 text-red-500" />,
        border: 'border-red-100 dark:border-red-900/30',
        progress: 'bg-red-500'
    },
    warning: {
        icon: <AlertTriangle className="w-5 h-5 text-amber-500" />,
        border: 'border-amber-100 dark:border-amber-900/30',
        progress: 'bg-amber-500'
    },
    info: {
        icon: <Info className="w-5 h-5 text-brand-500" />,
        border: 'border-brand-100 dark:border-brand-900/30',
        progress: 'bg-brand-500'
    }
};

const ToastItem = ({ id, title, description, type = 'default', duration, onRemove, action }) => {
    useEffect(() => {
        if (!duration) return;

        const timer = setTimeout(() => {
            onRemove(id);
        }, duration);

        return () => clearTimeout(timer);
    }, [id, duration, onRemove]);

    const style = variants[type] || variants.default;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
            className={cn(
                "pointer-events-auto w-full max-w-sm overflow-hidden rounded-xl border bg-white p-4 shadow-xl shadow-gray-200/50 ring-1 ring-black/5",
                "dark:bg-gray-900 dark:shadow-none dark:ring-white/10",
                style.border,
                "flex items-start gap-3"
            )}
        >
            <div className="flex-shrink-0 mt-0.5">
                {style.icon}
            </div>

            <div className="flex-1 w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                    {title}
                </p>
                {description && (
                    <p className="mt-1 text-[13px] leading-relaxed text-gray-500 dark:text-gray-400">
                        {description}
                    </p>
                )}
                {action && (
                    <div className="mt-3">
                        {action}
                    </div>
                )}
            </div>

            <div className="flex-shrink-0 flex ml-2">
                <button
                    onClick={() => onRemove(id)}
                    className="group rounded-lg p-1 text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                    <span className="sr-only">Close</span>
                    <X className="h-4 w-4 transition-transform group-hover:scale-110" />
                </button>
            </div>
        </motion.div>
    );
};

export const ToastContainer = ({ toasts, removeToast }) => {
    return (
        <div
            aria-live="assertive"
            className="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-end sm:p-6 z-[9999] pointer-events-none"
        >
            <div className="flex w-full flex-col items-center space-y-3 sm:items-end">
                <AnimatePresence mode="popLayout">
                    {toasts.map((toast) => (
                        <ToastItem key={toast.id} {...toast} onRemove={removeToast} />
                    ))}
                </AnimatePresence>
            </div>
        </div>
    );
};
